import { useMemo } from 'react';
import { UploadOutlined } from '@ant-design/icons';
import { Button, Typography, Upload } from 'antd';
import { RcFile, UploadChangeParam } from 'antd/es/upload';
import { MEDIA_ASSET_IMAGE_MIME_TYPES } from '@/entities/media-asset';
import { useNotificationContext } from '@/features/notifications';
import { Col } from '@/shared/ui';

interface ImageEditFormUploadProps {
  file?: RcFile;
  onBeforeUpload: (file: RcFile) => void;
  onChange: (info: UploadChangeParam) => void;
  onRemove: () => void;
}

const { Text } = Typography;

export const ImageEditFormUpload = ({
  file,
  onBeforeUpload,
  ...rest
}: ImageEditFormUploadProps) => {
  const { showNotification } = useNotificationContext();
  const filelist = useMemo(() => (file ? [file] : []), [file]);

  const beforeUpload = (file: RcFile) => {
    const isSupportedType = Object.keys(MEDIA_ASSET_IMAGE_MIME_TYPES).includes(
      file.type,
    );
    if (!isSupportedType) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: `You can only upload ${Object.values(MEDIA_ASSET_IMAGE_MIME_TYPES).join('/').toUpperCase()} file!`,
      });
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      showNotification({
        type: 'error',
        message: 'Error uploading image',
        description: 'Image must be smaller than 10MB!',
      });
    }
    if (isLt10M && isSupportedType) {
      onBeforeUpload(file);
    }
    return false;
  };

  return (
    <Col alignItems="flex-start">
      <Text
        style={{
          fontSize: 11,
          marginTop: -7,
          marginBottom: 10,
          opacity: 0.7,
        }}
      >
        Supported file types:{' '}
        {Object.values(MEDIA_ASSET_IMAGE_MIME_TYPES).join(', ')}
      </Text>
      <Upload
        name="file"
        fileList={filelist}
        listType="text"
        showUploadList={true}
        maxCount={1}
        beforeUpload={beforeUpload}
        {...rest}
      >
        <Button icon={<UploadOutlined />} style={{ marginTop: 10 }}>
          Select File
        </Button>
      </Upload>
    </Col>
  );
};

ImageEditFormUpload.displayName = 'ImageEditFormUpload';
